import { USER_ROLES } from "@repo/shared";
import { createContext, useCallback, useContext, useMemo, useRef } from "react";

import { useActivateSubscription } from "~/api/mutations/useActivateSubscription";
import { useRegisterBuyer } from "~/api/mutations/useRegisterBuyer";
import { useRegisterGuardian } from "~/api/mutations/useRegisterGuardian";
import { useRegisterStudent } from "~/api/mutations/useRegisterStudent";

import type { PropsWithChildren } from "react";

interface ActivationTokens {
  activationToken?: string;
  guardianToken?: string;
  studentToken?: string;
  buyerToken?: string;
}

interface SubscriptionActivationContextType {
  saveTokensFromUrl: (searchParams: URLSearchParams) => void;
  executeActivationIfAvailable: (userId: string, userRole?: string) => Promise<void>;
  clearTokens: () => void;
  hasStoredTokens: () => boolean;
}

const STORAGE_KEY = "subscription-activation-tokens";

const SubscriptionActivationContext = createContext<SubscriptionActivationContextType | null>(null);

export const SubscriptionActivationProvider = ({ children }: PropsWithChildren) => {
  const activateSubscription = useActivateSubscription();
  const registerBuyer = useRegisterBuyer();
  const registerGuardian = useRegisterGuardian();
  const registerStudent = useRegisterStudent();
  const hasExecutedForUser = useRef<string | null>(null);

  const saveTokensFromUrl = useCallback((searchParams: URLSearchParams) => {
    const activationToken = searchParams.get("activationToken");
    const guardianToken = searchParams.get("guardianToken");
    const studentToken = searchParams.get("studentToken");
    const buyerToken = searchParams.get("buyerToken");

    localStorage.removeItem(STORAGE_KEY);

    if (guardianToken) {
      const tokens: ActivationTokens = { guardianToken };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));

      return;
    }

    if (buyerToken) {
      const tokens: ActivationTokens = { buyerToken };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));

      return;
    }

    if (activationToken || studentToken) {
      const tokens: ActivationTokens = {};
      if (activationToken) tokens.activationToken = activationToken;
      if (studentToken) tokens.studentToken = studentToken;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
    }
  }, []);

  const clearTokens = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const hasStoredTokens = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return !!stored;
  }, []);

  const executeActivationIfAvailable = useCallback(
    async (userId: string, userRole?: string) => {
      if (!userId) return;
      if (hasExecutedForUser.current === userId) return;

      const storedTokens = localStorage.getItem(STORAGE_KEY);
      if (!storedTokens) return;

      hasExecutedForUser.current = userId;

      try {
        const tokens: ActivationTokens = JSON.parse(storedTokens);

        if (tokens.guardianToken && tokens.activationToken) {
          clearTokens();
          return;
        }

        if (tokens.guardianToken) {
          if (userRole && userRole !== USER_ROLES.GUARDIAN) {
            console.warn(
              "⚠️ Only guardians can register as guardians. This link is meant for the guardian account, not the student account. Please use the guardian's account to register.",
            );
            clearTokens();
            return;
          }

          const guardianResult = await registerGuardian
            .mutateAsync({
              guardianToken: tokens.guardianToken,
              userId: userId,
            })
            .catch((error) => {
              console.error("Failed to register guardian:", error);
              return null;
            });

          if (guardianResult !== null) {
            clearTokens();
          }
          return;
        }

        if (tokens.buyerToken) {
          if (userRole && userRole !== USER_ROLES.GUARDIAN) {
            console.warn(
              "⚠️ Only guardians can register as buyers. This link is meant for the guardian account, not the student account. Please use the guardian's account to register.",
            );
            clearTokens();
            return;
          }

          const buyerResult = await registerBuyer
            .mutateAsync({
              buyerToken: tokens.buyerToken,
            })
            .catch((error) => {
              console.error("Failed to register buyer:", error);
              return null;
            });

          if (buyerResult !== null) {
            clearTokens();
          }
          return;
        }

        if (tokens.studentToken && !tokens.activationToken) {
          if (userRole && userRole !== USER_ROLES.STUDENT) {
            console.warn(
              "⚠️ Only students can register with student token. This link is meant for the student account, not the guardian account. Please use the student's account to register.",
            );
            clearTokens();
            return;
          }

          const studentResult = await registerStudent
            .mutateAsync({
              studentToken: tokens.studentToken,
              userId: userId,
            })
            .catch((error) => {
              console.error("Failed to register student:", error);
              return null;
            });

          if (studentResult !== null) {
            clearTokens();
          }
          return;
        }

        // Legacy flow: activation token (deprecated, kept for backwards compatibility)
        if (tokens.activationToken) {
          if (userRole && userRole !== USER_ROLES.STUDENT) {
            console.warn(
              "⚠️ Only students can activate subscriptions. This link is meant for the student account, not the guardian account. Please use the student's account to activate.",
            );
            clearTokens();
            return;
          }

          const activationResult = await activateSubscription
            .mutateAsync({
              token: tokens.activationToken,
              userId: userId,
              studentToken: tokens.studentToken || undefined,
            })
            .catch((error) => {
              console.error("Failed to activate subscription:", error);
              return null;
            });

          if (activationResult?.success === true) {
            clearTokens();
          }
        }
      } catch (error) {
        console.error("Failed to parse stored activation tokens:", error);
        clearTokens();
      }
    },
    [activateSubscription, registerBuyer, registerGuardian, registerStudent, clearTokens],
  );

  const contextValue = useMemo(
    () => ({
      saveTokensFromUrl,
      executeActivationIfAvailable,
      clearTokens,
      hasStoredTokens,
    }),
    [saveTokensFromUrl, executeActivationIfAvailable, clearTokens, hasStoredTokens],
  );

  return (
    <SubscriptionActivationContext.Provider value={contextValue}>
      {children}
    </SubscriptionActivationContext.Provider>
  );
};

export const useSubscriptionActivation = () => {
  const context = useContext(SubscriptionActivationContext);

  if (!context) {
    throw new Error(
      "useSubscriptionActivation must be used within a SubscriptionActivationProvider",
    );
  }

  return context;
};
